import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config/canonical';
import { NxPanel, NxButton, NxChip } from '../components/nx';
import {
  Shield, ShieldCheck, Landmark, Building2, Search,
  ExternalLink, Phone, MapPin, Plus, Pencil, Trash2, X, Save,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Dashboard 3.0 — Texas Directory Center.
 *
 * Tabbed (Police / Sheriff / Court / Jail) directory with search by name +
 * county filter. Admins can add / edit / delete entries inline. Per
 * production directive, NO entries are seeded — admins populate manually.
 */
const TABS = [
  { id: 'police',  label: 'Police',   icon: Shield },
  { id: 'sheriff', label: 'Sheriff',  icon: ShieldCheck },
  { id: 'court',   label: 'Courts',   icon: Landmark },
  { id: 'jail',    label: 'Jails',    icon: Building2 },
];

const EMPTY_FORM = {
  name: '',
  phone: '',
  address: '',
  website: '',
  county: '',
  state: 'TX',
  notes: '',
  court_type: '',
  visitation: '',
};

export default function DirectoriesPage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'police');
  const [entries, setEntries] = useState([]);
  const [counties, setCounties] = useState([]);
  const [county, setCounty] = useState('');
  const [q, setQ] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | entryObject
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const token = () => localStorage.getItem('nexus_token');

  const fetchEntries = useCallback(async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      qs.set('category', tab);
      if (q) qs.set('q', q);
      if (county) qs.set('county', county);
      const res = await fetch(`${BACKEND_URL}/api/directories?${qs}`,
        { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEntries(data.entries || []);
      setCounties(data.counties || []);
      setCanEdit(!!data.user_can_edit);
    } catch (e) { toast.error(`Failed: ${e.message}`); }
    finally { setBusy(false); }
  }, [tab, q, county]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleTab = (id) => { setTab(id); setParams({ tab: id }); };

  const startNew = () => { setEditing('new'); setForm({ ...EMPTY_FORM }); };
  const startEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || '', phone: e.phone || '', address: e.address || '',
      website: e.website || '', county: e.county || '', state: e.state || 'TX',
      notes: e.notes || '', court_type: e.court_type || '', visitation: e.visitation || '',
    });
  };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const isNew = editing === 'new';
      const url = isNew
        ? `${BACKEND_URL}/api/directories`
        : `${BACKEND_URL}/api/directories/${editing.id}`;
      const body = { ...form, category: tab };
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success(isNew ? 'Entry added' : 'Entry updated');
      cancelEdit();
      fetchEntries();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/directories/${e.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) { toast.error(`Delete failed: ${err.message}`); }
  };

  const currentTabLabel = TABS.find(t => t.id === tab)?.label || tab;

  return (
    <div className="nx" data-testid="directories-page">
      <NxPanel
        title="Texas Directory Center"
        subtitle="LAW ENFORCEMENT · COURT · JAIL CONTACTS"
        action={canEdit ? (
          <NxButton variant="primary" className="nx-btn--sm" onClick={startNew} data-testid="dir-new">
            <Plus size={14} /> Add Entry
          </NxButton>
        ) : null}
      >
        {/* Tabs */}
        <div role="tablist" data-testid="dir-tabs"
          style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--nx-border-soft)', marginBottom: 12, overflowX: 'auto' }}>
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              onClick={() => handleTab(id)}
              data-testid={`dir-tab-${id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', fontSize: 12, fontWeight: 700,
                background: 'transparent',
                border: 'none', borderBottom: id === tab ? '2px solid #F4C04A' : '2px solid transparent',
                color: id === tab ? '#F4C04A' : '#94A3B8',
                cursor: 'pointer', letterSpacing: '0.02em', whiteSpace: 'nowrap',
              }}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input
              type="text"
              placeholder="Search by name or address…"
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="dir-search"
              style={{ width: '100%', padding: '8px 8px 8px 30px', background: '#0E1626', border: '1px solid var(--nx-border-soft)', borderRadius: 6, color: '#E2E8F0', fontSize: 13 }}
            />
          </div>
          <select
            value={county}
            onChange={e => setCounty(e.target.value)}
            data-testid="dir-county"
            style={{ padding: '8px 10px', background: '#0E1626', border: '1px solid var(--nx-border-soft)', borderRadius: 6, color: '#E2E8F0', fontSize: 13 }}>
            <option value="">All counties</option>
            {counties.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Editor */}
        {editing && canEdit && (
          <div className="nx-panel" data-testid="dir-editor"
            style={{ padding: 12, marginBottom: 12, background: 'rgba(244,192,74,0.04)', borderColor: 'rgba(244,192,74,0.20)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <p className="nx-eyebrow" style={{ margin: 0 }}>
                {editing === 'new' ? `New ${currentTabLabel} entry` : `Editing ${editing.name}`}
              </p>
              <NxButton variant="ghost" className="nx-btn--sm" onClick={cancelEdit}><X size={13} /></NxButton>
            </div>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
              <Field label="Name *" value={form.name}     onChange={v => setForm(f => ({ ...f, name: v }))}     testid="dir-form-name" />
              <Field label="County"  value={form.county}   onChange={v => setForm(f => ({ ...f, county: v }))}   testid="dir-form-county" />
              <Field label="Phone"   value={form.phone}    onChange={v => setForm(f => ({ ...f, phone: v }))}    testid="dir-form-phone" />
              <Field label="Website" value={form.website}  onChange={v => setForm(f => ({ ...f, website: v }))}  testid="dir-form-website" />
              <Field label="Address" value={form.address}  onChange={v => setForm(f => ({ ...f, address: v }))}  testid="dir-form-address" full />
              {tab === 'court' && (
                <Field label="Court type" value={form.court_type} onChange={v => setForm(f => ({ ...f, court_type: v }))} testid="dir-form-court-type" />
              )}
              {tab === 'jail' && (
                <Field label="Visitation" value={form.visitation} onChange={v => setForm(f => ({ ...f, visitation: v }))} testid="dir-form-visitation" full />
              )}
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
              <NxButton variant="primary" className="nx-btn--sm" onClick={save} disabled={saving} data-testid="dir-save">
                <Save size={13} /> {saving ? 'Saving…' : 'Save Entry'}
              </NxButton>
              <NxButton variant="ghost" className="nx-btn--sm" onClick={cancelEdit} data-testid="dir-cancel">Cancel</NxButton>
            </div>
          </div>
        )}

        {/* Entries */}
        {busy ? (
          <p style={{ color: '#94A3B8', fontSize: 13 }}>Loading…</p>
        ) : entries.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94A3B8' }} data-testid="dir-empty">
            <p style={{ fontSize: 13 }}>No {currentTabLabel.toLowerCase()} entries yet.</p>
            {canEdit ? (
              <p style={{ fontSize: 11, marginTop: 6 }}>Click <strong>Add Entry</strong> to populate this directory.</p>
            ) : (
              <p style={{ fontSize: 11, marginTop: 6 }}>Contact an administrator to populate this directory.</p>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }} data-testid="dir-list">
            {entries.map(e => (
              <article key={e.id} className="nx-panel" data-testid={`dir-entry-${e.id}`}
                style={{ padding: 12, background: 'rgba(11,20,38,0.5)', position: 'relative' }}>
                <p style={{ fontSize: 14, fontWeight: 800, color: '#F8FAFC', margin: 0, paddingRight: canEdit ? 60 : 0 }}>{e.name}</p>
                {canEdit && (
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
                    <button type="button" onClick={() => startEdit(e)} data-testid={`dir-edit-${e.id}`}
                      title="Edit"
                      style={{ background: 'transparent', border: '1px solid var(--nx-border-soft)', borderRadius: 4, padding: 4, color: '#94A3B8', cursor: 'pointer' }}>
                      <Pencil size={12} />
                    </button>
                    <button type="button" onClick={() => remove(e)} data-testid={`dir-del-${e.id}`}
                      title="Delete"
                      style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.30)', borderRadius: 4, padding: 4, color: '#FCA5A5', cursor: 'pointer' }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                  {e.county && <NxChip tone="amber">{e.county} County</NxChip>}
                  {e.court_type && <NxChip tone="cyan">{e.court_type}</NxChip>}
                </div>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#CBD5E1' }}>
                  {e.address && (
                    <span style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <MapPin size={12} style={{ marginTop: 2, color: '#94A3B8', flex: 'none' }} /> {e.address}
                    </span>
                  )}
                  {e.phone && (
                    <a href={`tel:${e.phone}`} style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#F4C04A', textDecoration: 'none' }}>
                      <Phone size={12} /> {e.phone}
                    </a>
                  )}
                  {e.website && (
                    <a href={e.website} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#F4C04A', textDecoration: 'none' }}>
                      <ExternalLink size={12} /> Website
                    </a>
                  )}
                  {e.visitation && (
                    <p style={{ fontSize: 11, color: '#94A3B8', margin: '4px 0 0' }}>Visitation: {e.visitation}</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </NxPanel>
    </div>
  );
}

function Field({ label, value, onChange, testid, full }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: full ? '1 / -1' : 'auto' }}>
      <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: '#94A3B8', letterSpacing: '0.10em', textTransform: 'uppercase' }}>{label}</span>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        style={{ padding: '8px 10px', background: '#0E1626', border: '1px solid var(--nx-border-soft)', borderRadius: 6, color: '#E2E8F0', fontSize: 13 }}
      />
    </label>
  );
}
